/**
 * useOrderUpdates Hook
 * 
 * Manages real-time order updates via WebSocket subscription.
 * Automatically subscribes to order-updates channel and filters
 * events by the authenticated user's ID.
 * 
 * Usage:
 *   const { connectionState } = useOrderUpdates();
 * 
 * This hook should be used once per app (in App.tsx or a top-level page).
 */

import { useEffect, useRef, useState } from 'react';
import { wsClient, WS_CHANNELS } from '@/lib/ws';
import { useOrdersStore } from '@/store/orders';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toasts';
import { logger } from '@/lib/logger';
import { orderSounds, isSoundEnabled } from '@/lib/sounds';

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

interface OrderUpdateEvent {
  order_id: string;
  status?: string;
  prep_eta_at?: string;
  rider_id?: string;
  rider_name?: string;
  customer_id?: string;
  [key: string]: any;
}

/**
 * Map order status to user-friendly notification and sound
 */
function getStatusNotification(status: string): { message: string | null; sound: (() => void) | null } {
  const statusMap: Record<string, { message: string; sound: () => void }> = {
    pending: { message: 'Order placed successfully! 🎉', sound: orderSounds.orderPlaced },
    accepted: { message: 'Vendor accepted your order! 👨‍🍳', sound: orderSounds.orderAccepted },
    preparing: { message: 'Your order is being prepared 🔥', sound: orderSounds.orderReady },
    ready_for_pickup: { message: 'Order is ready for pickup! 📦', sound: orderSounds.orderReady },
    rider_enroute: { message: 'Rider is on the way to pick up! 🏍️', sound: orderSounds.riderAssigned },
    picked_up: { message: 'Rider picked up your order! 📦', sound: orderSounds.riderAssigned },
    out_for_delivery: { message: 'Your order is out for delivery! 🚚', sound: orderSounds.outForDelivery },
    delivered: { message: 'Order delivered! Enjoy your meal! ✨', sound: orderSounds.delivered },
    cancelled: { message: 'Order cancelled ❌', sound: orderSounds.cancelled },
  };

  return statusMap[status] || { message: null, sound: null };
}

export function useOrderUpdates() {
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const updateOrderStatus = useOrdersStore((state) => state.updateOrderStatus);
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const addToast = useToastStore((state) => state.addToast);
  const handlerRef = useRef<((data: OrderUpdateEvent) => void) | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setConnectionState('idle');
      return;
    }

    // Check if offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setConnectionState('disconnected');
      logger.info('Browser offline, skipping WebSocket connection');
      return;
    }

    // Connect to WebSocket
    setConnectionState('connecting');
    
    try {
      wsClient.connect();
      
      // Define event handler
      const handleOrderUpdate = (data: OrderUpdateEvent) => {
        logger.info('Order update received', { orderId: data.order_id, status: data.status });
        
        // Filter: Only process events for this user
        if (data.customer_id && data.customer_id !== user.id) {
          logger.debug('Ignoring order update for different customer', { 
            eventCustomerId: data.customer_id,
            currentUserId: user.id,
          });
          return;
        }

        // Update order in store
        if (data.order_id && data.status) {
          updateOrderStatus(data.order_id, data.status);

          // Show toast notification and play sound
          const { message, sound } = getStatusNotification(data.status);
          if (message) {
            addToast('info', message);
          }
          if (sound && isSoundEnabled()) {
            sound();
          }
        }
      };

      // Store handler ref for cleanup
      handlerRef.current = handleOrderUpdate;

      // Subscribe to order updates channel
      wsClient.subscribe(WS_CHANNELS.ORDERS_BUYER, handleOrderUpdate);
      
      setConnectionState('connected');
      logger.info('Subscribed to order updates', { channel: WS_CHANNELS.ORDERS_BUYER });

    } catch (error) {
      logger.error('Failed to connect to WebSocket', error as Error);
      setConnectionState('error');
    }

    // Cleanup on unmount or auth change
    return () => {
      if (handlerRef.current) {
        wsClient.unsubscribe(WS_CHANNELS.ORDERS_BUYER, handlerRef.current);
        logger.info('Unsubscribed from order updates');
        handlerRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, updateOrderStatus]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      logger.info('Browser back online, reconnecting WebSocket');
      setConnectionState('connecting');
      wsClient.connect();
    };

    const handleOffline = () => {
      logger.info('Browser offline, disconnecting WebSocket');
      setConnectionState('disconnected');
      wsClient.disconnect();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
  };
}

